// App.js
import React from 'react';
import { Routes, Route } from "react-router-dom";
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import GlobalStateProvider from "./Contexts/GlobalStateProvider";
import { CssBaseline, Box } from '@mui/material';
import './App.css';
import './Styles/sznostyles.css';

// Components
import PasswordReset from "./Components/Administration/PasswordReset";
import PasswordResetConfirm from "./Components/Administration/PasswordResetConfirm";
import News from "./Components/Szemle/News";
import KnowledgeBase from "./Components/Kutfok/KnowledgeBase";
import Academy from "./Components/Akademia/Academy";
import BuiltHeritage from "./Components/Latnivalok/BuiltHeritage";
import Bothanical from "./Components/Latnivalok/Bothanical";
import Ethnographic from "./Components/Latnivalok/Ethnographic";
import Tracking from "./Components/Latnivalok/Tracking";
import Spiritual from "./Components/Latnivalok/Spiritual";
import Equestrian from "./Components/Latnivalok/Equestrian";
import RunicScripts from "./Components/Latnivalok/RunicScripts";
import Geologic from "./Components/Latnivalok/Geologic";
import Institutions from "./Components/Intezmenyek/Institutions";
import InstitutionDetail from "./Components/Business/CompanyDetail";
import Companies from "./Components/Business/Companies";
import CompanyDetail from "./Components/Business/CompanyDetail";
import Masters from "./Components/Business/Masters";
import MasterDetail from "./Components/Business/MasterDetail";
import RecommendBusiness from "./Components/Business/RecommendBusiness";
import DayLabourers from "./Components/Business/DayLabourers";
import DayLabourerDetail from "./Components/Business/DayLabourerDetail";
import RegisterBusiness from "./Components/Business/RegisterBusiness";
import UpdateBusiness from "./Components/Business/UpdateBusiness";
import Market from "./Components/Piac/Market";
import Listings from "./Components/Piac/Listings";
import AllPropertyListings from "./Components/Piac/AllPropertyListings";
import AllCarListings from "./Components/Piac/AllCarListings";
import WinterSportsListings from "./Components/Piac/WinterSportsListings";
import SummerSportsListings from "./Components/Piac/SummerSportsListings";
import OwnProduceListings from "./Components/Piac/OwnProduceListings";
import ProducerList from "./Components/Piac/ProducerList";
import ProducerDetail from "./Components/Piac/ProducerDetail";
import AdvertisementLinks from "./Components/Piac/AdvertisementLinks";
import SzeklerlandEvents from "./Components/Piac/SzeklerlandEvents";
import AccommodationMarket from "./Components/Szallas/AccommodationMarket";
import AllAccommodationListings from "./Components/Szallas/AllAccommodationListings";
import MarketplaceAccommodationDetail from "./Components/Szallas/MarketplaceAccommodationDetail";
import AddAccommodation from "./Components/Szallas/AddAccommodation";
import EditAccommodation from "./Components/Szallas/EditAccommodation";
import AddCar from "./Components/Piac/Add/AddCar";
import AddWinterSportItem from "./Components/Piac/Add/AddWinterSportItem";
import AddSummerSportItem from "./Components/Piac/Add/AddSummerSportItem";
import AddOwnCrop from "./Components/Piac/Add/AddOwnCrop";
import ListingDetail from "./Components/Piac/ListingDetail";
import AddProperty from "./Components/Piac/AddProperty";
import ListingUpdate from "./Components/Piac/ListingUpdate";
import AccountCreated from "./Components/Administration/AccountCreated";
import MineralWaterDetail from "./Components/Asvanyviz/MineralWaterDetail";
import Activation from "./Components/Administration/Activation";
import Login from "./Components/Administration/Login";
import Register from "./Components/Administration/Register";
import Profile from "./Components/Administration/Profile";
import CookieBanner from './Components/Administration/CookieBanner';
import AddMapObject from "./Components/AddMapObject";
import AddSight from "./Components/Latnivalok/Add/AddSight";
import AddThematicPoint from "./Components/ThematicMaps/AddThematicPoint";
import AddRunningRoute from "./Components/ThematicMaps/AddRunningRoute";
import EditThematicPoint from "./Components/ThematicMaps/EditThematicPoint";
import Home from "./Components/Home";
import FamilyManagerHome from "./Components/FamilyManager/FamilyManagerHome";
import FamilyTreeHome from "./Components/FamilyTree/FamilyTreeHome";
import FamilyTreeEditor from "./Components/FamilyTree/FamilyTreeEditor";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import ThematicMap from "./Components/ThematicMaps/ThematicMap";
import PrivacyPolicy from './Components/Policy/PrivacyPolicy';
import MobilePrivacyPolicy from './Components/Policy/MobilePrivacyPolicy';
import Impresszum from './Components/Policy/Impresszum';
import PolicyPage from './Components/Policy/PolicyPage';
import Cookies from './Components/Policy/Cookies';
import SEO from './Components/SEO';
import Layout from './Components/Layout';
import ProtectedRoute from './Routes/ProtectedRoute';
import AcademyGuide from "./Components/Guides/AcademyGuide";
import DictionaryGuide from "./Components/Guides/DictionaryGuide";
import MapGuide from "./Components/Guides/MapGuide";
import MarketGuide from "./Components/Guides/MarketGuide";
import ReportIssue from "./Components/Contact/ReportIssue";
import WriteToUs from "./Components/Contact/WriteToUs";
import VisitsPage from "./Components/Policy/VisitsPage";
import { usePageTracking } from './Hooks/usePageTracking';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, Navigate } from 'react-router-dom';

const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  const supportedLanguages = React.useMemo(() => ['hu', 'en', 'ro', 'de', 'fr', 'es', 'it'], []);

  const getCleanLang = (langCode) => {
    if (!langCode) return 'hu';
    const baseLang = langCode.split('-')[0].toLowerCase();
    return supportedLanguages.includes(baseLang) ? baseLang : 'hu';
  };

  React.useEffect(() => {
    const cleanLang = getCleanLang(lang);
    if (lang && i18n.language !== lang && supportedLanguages.includes(lang)) {
      i18n.changeLanguage(lang);
    } else if (lang && !supportedLanguages.includes(lang)) {
        // If lang is something like en-US, but we only support en
        const baseLang = lang.split('-')[0].toLowerCase();
        if (supportedLanguages.includes(baseLang)) {
            i18n.changeLanguage(baseLang);
        }
    }
  }, [lang, i18n, supportedLanguages]);
  
  // If no lang prefix, i18next-browser-languagedetector might have detected it or it uses fallback
  // For SEO, we might want to redirect to a prefixed URL
  if (!lang && (location.pathname === '/' || location.pathname === '')) {
    return <Navigate to={`/${getCleanLang(i18n.language)}`} replace />;
  }

  // If there's a first segment that is NOT a supported language, redirect to include the language
  const pathSegments = location.pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0 && !supportedLanguages.includes(pathSegments[0])) {
    const currentPath = location.pathname;
    const cleanLang = getCleanLang(i18n.language);
    const langPrefix = `/${cleanLang}`;
    
    // Check if the current language prefix is already there to avoid infinite loops
    // (Though !supportedLanguages.includes(pathSegments[0]) should handle this)
    if (currentPath.startsWith(langPrefix + '/')) {
      return children;
    }

    // Special case: if the path contains a language code NOT as the first segment
    // (e.g., /mester-AJANLASA/en/...)
    const foundLangIndex = pathSegments.findIndex(seg => supportedLanguages.includes(seg));
    if (foundLangIndex > 0) {
      const detectedLang = pathSegments[foundLangIndex];
      const realPath = '/' + pathSegments.slice(foundLangIndex + 1).join('/');
      return <Navigate to={`/${detectedLang}${realPath === '/' ? '' : realPath}`} replace />;
    }

    // Special case: if the path starts with mester-AJANLASA (case insensitive)
    // and it seems to be duplicated or wrongly prefixed by the server
    const thematicPaths = [
      'turista-utvonalak',
      'foldtani-terkep',
      'nepviselet-terkep',
      'nepi-epiteszet-terkep',
      'panteon-terkep',
      'futuristique-terkep',
      'szekelykapu-terkep',
      'ballada-terkep',
      'nepdal-terkep',
      'locsoloversek-terkep',
      'mester-ajanlasa',
      'ceg-ajanlasa',
      'napszamos-ajanlasa',
      'mester/szerkesztes',
      'ceg/szerkesztes',
      'napszamos/szerkesztes',
      'login',
      'register',
      'market',
      'terkep',
      'news',
      'kutfok',
      'academy',
      'cegek',
      'mesterek',
      'napszamosok',
      'accommodation-market',
      'csaladfaallitas'
    ];

    if (pathSegments.length > 0 && thematicPaths.some(tp => location.pathname.toLowerCase().includes(tp.toLowerCase()))) {
       const realPath = '/' + pathSegments.join('/');
       return <Navigate to={`${langPrefix}${realPath}`} replace />;
    }

    if (pathSegments[0].toLowerCase() === 'mester-ajanlasa' && pathSegments.length > 1) {
       // If it's something like /mester-AJANLASA/epitett-orokseg -> /hu/epitett-orokseg
       const realPath = '/' + pathSegments.slice(1).join('/');
       return <Navigate to={`${langPrefix}${realPath}`} replace />;
    }
    
    return <Navigate to={`${langPrefix}${currentPath}`} replace />;
  }

  return children;
};

const FamilyTreeRedirect = () => {
  const { id, token } = useParams();
  const { i18n } = useTranslation();
  return <Navigate to={`/${i18n.language}/csaladfaallitas/${id}/${token}`} replace />;
};

function App() {
  usePageTracking();
  const { t } = useTranslation();
  return (
      <GlobalStateProvider>
        <I18nextProvider i18n={i18n}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Header />
              <CookieBanner />
              <Box sx={{ pt: '64px', flex: 1 }}>
                <Routes>
                  <Route path="/:lang?" element={<LanguageWrapper><Layout /></LanguageWrapper>}>
                  <Route index element={<><SEO /><Home /></>} />
                  <Route path="login" element={<><SEO title={t('Administration.LoginTitle') + " | SZNO"} /><Login /></>} />
                  <Route path="register" element={<><SEO title={t('Administration.RegisterTitle') + " | SZNO"} /><Register /></>} />
                  <Route path="created" element={<AccountCreated />} />
                  <Route path="activation/:uid/:token" element={<Activation />} />
                  <Route path="password-reset" element={<PasswordReset />} />
                  <Route path="password-reset/:uid/:token" element={<PasswordResetConfirm />} />
                  <Route path="market" element={<><SEO title={t('SEO.Market.Title')} description={t('SEO.Market.Description')} /><Market /></>} />
                  <Route path="all-property-listings" element={<><SEO title={t('SubMenu.PropertyListing') + " | SZNO"} description={t('SEO.Property.Description', 'Böngésszen eladó és kiadó ingatlanok között Székelyföldön.')} /><AllPropertyListings /></>} />
                  <Route path="all-car-listings" element={<><SEO title={t('SubMenu.Car') + " | SZNO"} description={t('SEO.Car.Description', 'Eladó autók és gépjárművek Székelyföldön.')} /><AllCarListings /></>} />
                  <Route path="teli-sportfelszereles" element={<><SEO title={t('SubMenu.WinterSports') + " | SZNO"} description={t('SEO.WinterSports.Description', 'Téli sportfelszerelések: síléc, snowboard és kiegészítők.')} /><WinterSportsListings /></>} />
                  <Route path="nyari-sportfelszereles" element={<><SEO title={t('SubMenu.SummerSports') + " | SZNO"} description={t('SEO.SummerSports.Description', 'Nyári sportfelszerelések: bicikli, túrafelszerelés és kemping.')} /><SummerSportsListings /></>} />
                  <Route path="sajat-termeny" element={<><SEO title={t('SubMenu.OwnProduce') + " | SZNO"} description={t('SEO.OwnProduce.Description', 'Helyi termelők saját készítésű termékei közvetlenül a gazdától.')} /><OwnProduceListings /></>} />
                  <Route path="termelok" element={<><SEO title={t('Market.Producers') + " | SZNO"} description={t('SEO.Producers.Description', 'Ismerje meg Székelyföld helyi termelőit és kézműveseit.')} /><ProducerList /></>} />
                  <Route path="termelo/:id" element={<ProducerDetail />} />
                  <Route path="reklam" element={<><SEO title={t('Advertisement.Title') + " | SZNO"} description={t('SEO.Advertisement.Description', 'Hirdetési lehetőségek és kiemelt partneri ajánlatok.')} /><AdvertisementLinks /></>} />
                  <Route path="szekelyfoldi-esemenyek" element={<><SEO title={t('Events.SzeklerlandEvents') + " | SZNO"} description={t('SEO.Events.Description', 'Aktuális kulturális, sport és közösségi események Székelyföldön.')} /><SzeklerlandEvents /></>} />
                  <Route path="accommodation-market" element={<><SEO title={t('Header.Szallas') + " | SZNO"} description={t('SEO.Accommodation.Description', 'Szálláshelyek Székelyföldön: panziók, hotelek és vendégházak.')} /><AccommodationMarket /></>} />
                  <Route path="all-accommodation-listings" element={<><SEO title={t('Header.Szallas') + " | SZNO"} description={t('SEO.Accommodation.Description')} /><AllAccommodationListings /></>} />
                  <Route path="marketplace-accommodation/:id" element={<MarketplaceAccommodationDetail />} />
                  <Route path="marketplace-accommodation/:id/edit" element={<EditAccommodation />} />
                  <Route path="terkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="szallasterkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="autoterkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="ingatlanterkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="telisportfelszereles-terkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="nyarisportfelszereles-terkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="sajattermeny-terkep" element={<><SEO title={t('SEO.Map.Title')} description={t('SEO.Map.Description')} /><Listings /></>} />
                  <Route path="terkep/:id" element={<ListingDetail type="property" />} />
                  <Route path="property/:id" element={<ListingDetail type="property" />} />
                  <Route path="car/:id" element={<ListingDetail type="car" />} />
                  <Route path="listing-update/:type/:id" element={<ListingUpdate />} />
                  <Route path="turista-utvonalak" element={<><SEO title={t('Thematic.TouristRoutes') + " | SZNO"} description={t('SEO.TouristRoutes.Description', 'Székelyföld kijelölt turistaútvonalai és gyalogtúra ösvényei.')} /><ThematicMap type="routes" /></>} />
                  <Route path="foldtani-terkep" element={<><SEO title={t('Thematic.GeologicalMap') + " | SZNO"} description={t('SEO.GeologicalMap.Description', 'Székelyföld földtani térképe.')} /><ThematicMap category="foldtani" /></>} />
                  <Route path="nepviselet-terkep" element={<><SEO title={t('Thematic.FolkCostume') + " | SZNO"} description={t('SEO.FolkCostume.Description', 'Hagyományos székely népviseletek térképes gyűjteménye.')} /><ThematicMap category="nepviselet" /></>} />
                  <Route path="nepi-epiteszet-terkep" element={<><SEO title={t('Thematic.FolkArchitecture') + " | SZNO"} description={t('SEO.FolkArchitecture.Description', 'Népi építészeti örökségünk: tájházak, malmok és gazdasági épületek.')} /><ThematicMap category="nepi_epiteszet" /></>} />
                  <Route path="panteon-terkep" element={<><SEO title={t('Thematic.Pantheon') + " | SZNO"} description={t('SEO.Pantheon.Description', 'Székely nagyjaink emlékhelyei és történelmi panteonunk.')} /><ThematicMap category="panteon" /></>} />
                  <Route path="futuristique-terkep" element={<><SEO title={t('Thematic.Futuristique') + " | SZNO"} description={t('SEO.Futuristique.Description', 'Székelyföld jövőképe és fejlesztési projektjei.')} /><ThematicMap category="futuristique" /></>} />
                  <Route path="szekelykapu-terkep" element={<><SEO title={t('Thematic.Szekelykapu') + " | SZNO"} description={t('SEO.Szekelykapu.Description', 'Faragott székelykapuk gyűjteménye és története.')} /><ThematicMap category="szekelykapu" /></>} />
                  <Route path="ballada-terkep" element={<><SEO title={t('Thematic.Ballada') + " | SZNO"} description={t('SEO.Ballada.Description', 'Székely népballadák gyűjtőhelyei és változatai.')} /><ThematicMap category="ballada" /></>} />
                  <Route path="nepdal-terkep" element={<><SEO title={t('Thematic.Nepdal') + " | SZNO"} description={t('SEO.Nepdal.Description', 'Hagyományos székely népdalok és dallamok lelőhelyei.')} /><ThematicMap category="nepdal" /></>} />
                  <Route path="locsoloversek-terkep" element={<><SEO title={t('Thematic.Locsoloversek') + " | SZNO"} description={t('SEO.Locsoloversek.Description', 'Húsvéti locsolóversek és hagyományok Székelyföldön.')} /><ThematicMap category="locsoloversek" /></>} />
                  <Route path="mineral_water/:category/:id" element={<MineralWaterDetail />} />
                  <Route path="news" element={<><SEO title={t('SEO.News.Title')} description={t('SEO.News.Description')} /><News /></>} />
                  <Route path="csaladfaallitas" element={<><SEO title={t('Thematic.FamilyTree.Title') + " | SZNO"} /><FamilyTreeHome /></>} />
                  <Route path="family-manager" element={<><SEO title={t('FamilyManager.Title', 'Családmenedzser') + " | SZNO"} /><FamilyManagerHome /></>} />
                  <Route path="csaladfaallitas/:id/:token" element={<><SEO title={t('Thematic.FamilyTree.Title') + " | SZNO"} /><FamilyTreeEditor /></>} />
                  <Route path="family-tree/:id/:token" element={<FamilyTreeRedirect />} />
                  <Route path="kutfok" element={<><SEO title={t('KnowledgeBase.Title') + " | SZNO"} /><KnowledgeBase /></>} />
                  <Route path="academy" element={<><SEO title={t('SEO.Academy.Title')} description={t('SEO.Academy.Description')} /><Academy /></>} />
                  <Route path="epitett-orokseg" element={<BuiltHeritage />} />
                  <Route path="novenyvilag" element={<Bothanical />} />
                  <Route path="neprajzi" element={<Ethnographic />} />
                  <Route path="rovasemlek" element={<RunicScripts />} />
                  <Route path="gyalogtura" element={<Tracking />} />
                  <Route path="lovastura" element={<Equestrian />} />
                  <Route path="egyhazi" element={<Spiritual />} />
                  <Route path="geological-sights" element={<Geologic />} />
                  <Route path="institutions" element={<><SEO title={t('Header.Institutions') + " | SZNO"} /><Institutions /></>} />
                  <Route path="institutions/:id" element={<InstitutionDetail />} />
                  <Route path="cegek" element={<><SEO title={t('SEO.Business.Title')} description={t('SEO.Business.Description')} /><Companies /></>} />
                  <Route path="ceg/:id" element={<CompanyDetail />} />
                  <Route path="mesterek" element={<Masters />} />
                  <Route path="mester/:id" element={<MasterDetail />} />
                  <Route path="napszamosok" element={<DayLabourers />} />
                  <Route path="napszamos/:id" element={<DayLabourerDetail />} />
                  <Route path="uzleti-regisztracio" element={<RegisterBusiness />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy isMobile={false} />} />
                  <Route path="mobile-privacy-policy" element={<MobilePrivacyPolicy isMobile={false} />} />
                  <Route path="impresszum" element={<Impresszum />} />
                  <Route path="sutibeallitasok" element={<Cookies />} />
                  <Route path="latogatottsag" element={<VisitsPage />} />
                  <Route path=":policy" element={<PolicyPage />} />
                  <Route path="honismereti-terkep-utmutato" element={<MapGuide />}/>
                  <Route path="szekely-szotar-utmutato" element={<DictionaryGuide />}/>
                  <Route path="virtualis-akademia-utmutato" element={<AcademyGuide />}/>
                  <Route path="piacter-szabalyzat" element={<MarketGuide />}/>
                  <Route path="hibajelentes" element={<ReportIssue />}/>
                  <Route path="irjon-nekunk" element={<WriteToUs />}/>

                  <Route element={<ProtectedRoute />}>
                    <Route path="profile" element={<Profile />} />
                    <Route path="addproperty" element={<AddProperty />} />
                    <Route path="addcar" element={<AddCar />} />
                    <Route path="add-winter-sport" element={<AddWinterSportItem />} />
                    <Route path="add-summer-sport" element={<AddSummerSportItem />} />
                    <Route path="addaccommodation" element={<AddAccommodation />} />
                    <Route path="sajat-termeny-hozzaadasa" element={<AddOwnCrop />} />
                    <Route path="add-map-object" element={<AddMapObject />} />
                    <Route path="add-sight/:sightType" element={<AddSight />} />
                    <Route path="add-thematic/:category" element={<AddThematicPoint />} />
                    <Route path="add-running-route" element={<AddRunningRoute />} />
                    <Route path="edit-thematic/:category/:id" element={<EditThematicPoint />} />
                    <Route path="mester-ajanlasa" element={<RecommendBusiness />} />
                    <Route path="ceg-ajanlasa" element={<RecommendBusiness />} />
                    <Route path="napszamos-ajanlasa" element={<RecommendBusiness />} />
                    <Route path="mester/szerkesztes/:id" element={<UpdateBusiness />} />
                    <Route path="ceg/szerkesztes/:id" element={<UpdateBusiness />} />
                    <Route path="napszamos/szerkesztes/:id" element={<UpdateBusiness />} />
                  </Route>
                </Route>
              </Routes>
            </Box>
            <Footer />
          </Box>
        </I18nextProvider>
      </GlobalStateProvider>
  );
}
export default App;